import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Companies, Conditions } from "../../../_shared/interfaces";
import { ConditionStoreService } from "../../../_shared/services";
import { CommonModule, JsonPipe, NgIf } from "@angular/common";
import { RouterLink } from "@angular/router";
import { ClrCommonFormsModule, ClrInputModule, ClrSelectModule } from "@clr/angular";
import { getTenantStorage } from '../../../_shared/utils/tenant-context';
import { CountrySelectComponent } from '../../../_shared/components/country-select/country-select.component';

import { environment as env } from "../../../../environments/environment";
import { TranslocoDirective } from "@jsverse/transloco";

@Component({
  selector: 'app-contact-form',
  standalone: true,
  imports: [
    CountrySelectComponent,
    CommonModule,
    JsonPipe,
    RouterLink,
    NgIf,
    ReactiveFormsModule,
    ClrCommonFormsModule,
    ClrInputModule,
    ClrSelectModule,
    TranslocoDirective
  ],
  templateUrl: './contact-form.component.html',
  styleUrl: './contact-form.component.sass'
})
export class ContactFormComponent implements OnInit, OnChanges {
  public kontaktForm!: FormGroup;
  public iban!: FormControl;
  token = getTenantStorage('access_token');
  delivery_conditions: Conditions[] = [];
  payment_conditions: Conditions[] = [];
  devMode: boolean;

  @Input() newCompany: Companies | null = null;
  @Input() editing = false;
  @Output() submitContact = new EventEmitter<Companies>();

  constructor(
    private fb: FormBuilder,
    private condss: ConditionStoreService,
  ) {
    this.devMode = env.stages.dev;
  }

  ngOnInit() {
    this.initForm();
    this.loadPayConds();
    this.loadDeliveryConds();
  }

  ngOnChanges() {
    this.initForm();
    this.setFormValues(this.newCompany);
  }

  private setFormValues(newCompany: Companies | null) {
    if (!newCompany) {
      return;
    }
    this.kontaktForm.patchValue({
      ...newCompany,
      type: newCompany.contact_type ?? newCompany.type ?? '',
      country: this.normalizeCountry(newCompany.country)
    });
  }

  private initForm() {
    if (this.kontaktForm) { return; }

    this.iban = new FormControl('');

    this.kontaktForm = this.fb.group({
      active: true,
      bankcode: [''],
      bankname: [''],
      cid: [''],
      city: [''],
      classification: [''],
      company: [''],
      cond_delivery: [''],
      cond_payment: [''],
      country: ['DE'],
      department: [''],
      email: [''],
      email2: [''],
      email3: [''],
      iban: this.iban,
      nda: false,
      notes: [''],
      crm_id: [''],
      phone: [''],
      postcode: [''],
      region: [''],
      street: [''],
      tax_id: [''],
      type: [''],
      vat_id: [''],
      leitweg_id: [''],
      einvoice_endpoint: [''],
      website: ['']
    });
  }

  private normalizeCountry(country?: string | null): string {
    if (!country) return '';
    const trimmed = country.trim();
    if (trimmed.length === 2) {
      return trimmed.toUpperCase();
    }
    const map: Record<string, string> = {
      Belgien: 'BE',
      Dänemark: 'DK',
      Deutschland: 'DE',
      Frankreich: 'FR',
      Italien: 'IT',
      Kroatien: 'HR',
      Luxemburg: 'LU',
      Niederlande: 'NL',
      Österreich: 'AT',
      Polen: 'PL',
      Schweden: 'SE',
      Schweiz: 'CH',
      Tschechien: 'CZ',
      Russland: 'RU',
      Slowakei: 'SK',
      Slowenien: 'SI'
    };
    return map[trimmed] || trimmed;
  }

  loadPayConds() {
    this.condss.getConditionByType(2).subscribe(data => {
      if (!data) {
        return;
      }
      this.payment_conditions = data.object;
      // console.table(this.payment_conditions);
    });
  }

  loadDeliveryConds() {
    this.condss.getConditionByType(1).subscribe(data => {
      if (!data) {
        return;
      }
      this.delivery_conditions = data.object;
      // console.table(this.delivery_conditions);
    });
  }

  submitForm() {
    const formValue = this.kontaktForm.value;
    const newCompany: Companies = {
      ...formValue,
      active: this.normalizeBoolean(formValue.active),
      nda: this.normalizeBoolean(formValue.nda),
      crm_id: this.normalizeOptionalInteger(formValue.crm_id),
      cond_delivery: this.normalizeOptionalForeignKey(formValue.cond_delivery),
      cond_payment: this.normalizeOptionalForeignKey(formValue.cond_payment),
      contact_type: this.normalizeOptionalString(formValue.type),
    };

    delete (newCompany as Partial<Companies>).type;
    this.submitContact.emit(newCompany);
    this.kontaktForm.reset();
  }

  private normalizeOptionalInteger(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number.parseInt(trimmed, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private normalizeOptionalForeignKey(value: unknown): number | null {
    const normalized = this.normalizeOptionalInteger(value);
    if (normalized === null || normalized <= 0) {
      return null;
    }
    return normalized;
  }

  private normalizeBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = String(value).trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'ja';
  }

  private normalizeOptionalString(value: unknown): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }

    const trimmed = String(value).trim();
    return trimmed ? trimmed : undefined;
  }
}
